/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { gnoment, Gnoment } from 'shared-ui/gnoment';
import { Icon, FlexRow, FlexCol, Button, NumberField } from 'sarsaparilla';
import get from 'lodash/get';
import GroupMemberItem from './GroupMemberItem';
import GroupMemberItemWithDates from './GroupMemberItemWithDates';
import {
    createAvailabilityRangeMap,
    readRuleAsMoment,
    readRuleValue,
    getNewId,
    getDefaultPass,
    NoQuotaMods,
    filterValidationDataIndices,
    hasRule,
} from '../../utils/utilsNew';
import * as types from '../../constants';
import InfoModal from '../../../shared-components/common/InfoModal';
import { PERMIT } from '../../props/permit';

const propTypes = {
    permit: PERMIT,
    divisionId: PropTypes.string,
    entryDate: PropTypes.instanceOf(Gnoment),
    exitDate: PropTypes.shape({}),
    groupMembers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
    minMemberQuota: PropTypes.number,
    maxMemberQuota: PropTypes.number,
    maxGroupSize: PropTypes.number,
    maxGroupSizeCommercial: PropTypes.number,
    handleGroupMemberChange: PropTypes.func.isRequired,
    isDisabled: PropTypes.bool,
    passOptions: PropTypes.shape({}),
    disableGroupSizeChanges: PropTypes.bool,
    validation: PropTypes.shape({}),
    isQuotaModifyAllowed: PropTypes.bool,
    issuance: PropTypes.shape({}),
    onModifyDetails: PropTypes.func,
    validationRules: PropTypes.shape({}),
    isFirstGroupMemberDateDisabled: PropTypes.bool,
    isHighUseFeeApplicable: PropTypes.bool,
    readOnly: PropTypes.bool,
    modifyDetailsButtonText: PropTypes.string,
};

const defaultProps = {
    minMemberQuota: 1,
    maxMemberQuota: 100,
    validationRules: {},
    readOnly: false,
};

export class GroupMemberList extends React.Component {
    static propTypes = propTypes;
    static defaultProps = defaultProps;

    constructor(props) {
        super(props);

        // groupMembers used to remember groupMembers deleted unintentionally
        // ex. when changing the number of group members with the input box
        this.state = {
            groupMembers: props.groupMembers,
            focusOnNextAdd: false,
            showGroupSizeModal: false,
            alwaysDisplayPassOptions: false,
        };
    }

    componentDidMount() {
        const minRequired = Math.max(
            this.props.minMemberQuota,
            this.props.groupMembers.length
        );
        if (minRequired && minRequired > this.props.groupMembers.length) {
            this.handleGroupSizeChange({ target: { value: minRequired } });
        }
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.groupMembers.length < this.props.groupMembers.length) {
            this.setState({ groupMembers: this.props.groupMembers });
        }

        // Will update this when endDate gets updated from the exit Date component
        const newPermitHolderEndDate = gnoment(
            get(this.props, 'groupMembers[0].endDate', null)
        );
        const permitHolderEndDate = gnoment(
            get(prevProps, 'groupMembers[0].endDate', null)
        );
        if (
            newPermitHolderEndDate.isValid() &&
            !permitHolderEndDate.isSame(newPermitHolderEndDate)
        ) {
            this.setState({ groupMembers: this.props.groupMembers });
        }

        if (
            (hasRule('AdultYouthOnly', this.props.permit.rules) ||
                this.props.permit.id === types.PACK_CREEK_ID) &&
            !prevState.alwaysDisplayPassOptions
        ) {
            this.setState({ alwaysDisplayPassOptions: true });
        }
    }

    onChangeDetailsClick = () => {
        this.props.onModifyDetails(this.props.issuance);
    };

    getHeaderText() {
        const permitId = this.props.permit.id;
        let { maxMemberQuota } = this.props;

        if (permitId === '234623') {
            // Middle Fork
            // we allow "double" to be entered because a group member could leave midtrip and be replaced.
            maxMemberQuota = Math.floor(maxMemberQuota / 2);
        }

        switch (permitId) {
            case '234622': // Salmon River (4 Rivers)
            case '234623': // Middle Fork of the Salmon River
                return (
                    <p className="permit-group-header-text">
                        Add everyone that will be going with you. This can be changed at
                        any time before the permit is issued. Maximum of {maxMemberQuota}{' '}
                        members at this facility. Read{' '}
                        <Button
                            type="button"
                            appearance="link"
                            className="per-inline-button"
                            onClick={() => this.setState({ showGroupSizeModal: true })}
                        >
                            How Group Size Affects Your Stay Limits
                        </Button>{' '}
                        for more info.
                    </p>
                );
            default:
                return (
                    <p className="permit-group-header-text">
                        How many people will be going on this trip?
                    </p>
                );
        }
    }

    handleGroupSizeChange = (groupSize) => {
        const memberCount = this.props.groupMembers.length;
        const { minMemberQuota, maxMemberQuota } = this.props;
        let newMemberCount = +groupSize || 0;

        if (maxMemberQuota && newMemberCount > maxMemberQuota) {
            newMemberCount = maxMemberQuota;
        } else if (minMemberQuota && newMemberCount < minMemberQuota) {
            newMemberCount = minMemberQuota;
        }

        if (newMemberCount > memberCount) {
            this.addMember(newMemberCount - memberCount);
        } else if (newMemberCount < memberCount) {
            // remove members from end of list
            const newGroupMembers = [...this.props.groupMembers];

            if (newMemberCount <= 1) {
                // remove all members but the leader
                this.props.handleGroupMemberChange([newGroupMembers[0]]);
            } else {
                this.props.handleGroupMemberChange(
                    newGroupMembers.slice(0, newMemberCount)
                );
            }
        }
    };

    addMember = (numMembersToAdd) => {
        const { maxMemberQuota } = this.props;
        const currentCount = this.props.groupMembers.length;
        const newGroupMembers = this.state.groupMembers.slice(0, currentCount);

        let condition = false;
        let diff = 0;
        if (Number.isInteger(numMembersToAdd)) {
            // add the amount
            condition = currentCount + numMembersToAdd <= maxMemberQuota;
            diff = numMembersToAdd;

            this.setState({ focusOnNextAdd: false });
        } else {
            // add one
            condition = currentCount + 1 <= maxMemberQuota;
            diff = 1;

            this.setState({ focusOnNextAdd: true });
        }

        if (condition) {
            for (let i = 0; i < diff; i += 1) {
                const newMember = {
                    id: getNewId(),
                    removable: true,
                    startDate: this.props.entryDate,
                    endDate: this.props.exitDate ? this.props.exitDate : null,
                    fName: '',
                    mName: '',
                    lName: '',
                    remark: '',
                    passholderType: getDefaultPass(this.props.permit),
                };
                newGroupMembers.push(
                    this.state.groupMembers[currentCount + i] || newMember
                );
            }
        }
        this.props.handleGroupMemberChange(
            newGroupMembers,
            true,
            this.props.refreshValidations
        );
    };

    removeMember = (memberId) => {
        const { groupMembers } = this.state;
        const newGroupMembers = groupMembers.slice(0, this.props.groupMembers.length);
        newGroupMembers.splice(memberId, 1);

        // if user removes with intent, remove from the groupMember state
        this.setState({ groupMembers: newGroupMembers });
        this.props.handleGroupMemberChange(
            newGroupMembers,
            true,
            this.props.refreshValidations
        );
    };

    handleMemberInfoChange = (idx, field, input) => {
        const newGroupMembers = [...this.props.groupMembers];
        const foundMember = newGroupMembers[idx];
        let affectsLineItems = false;

        if (foundMember) {
            switch (field) {
                case 'memberFName':
                    foundMember.fName = input;
                    break;
                case 'memberMName':
                    foundMember.mName = input;
                    break;
                case 'memberLName':
                    foundMember.lName = input;
                    break;
                case 'memberRemark':
                    foundMember.remark = input;
                    break;
                case 'memberBirthdate':
                    foundMember.birthdate = input;
                    affectsLineItems = true;
                    break;
                case 'memberAge':
                    foundMember.age = parseInt(input, 10);
                    affectsLineItems = true;
                    break;
                case 'memberArriving':
                    foundMember.startDate = input;
                    affectsLineItems = true;
                    break;
                case 'memberDeparting':
                    foundMember.endDate = input;
                    affectsLineItems = true;
                    break;
                case 'memberPassholderId':
                    foundMember.passholderId = input;
                    break;
                case 'memberPassholderType':
                    foundMember.passholderType = input;
                    affectsLineItems = true;
                    break;
                default:
                    break;
            }

            // update both the state groupMember and the actual when info changes
            this.setState({ groupMembers: newGroupMembers });
            this.props.handleGroupMemberChange(
                newGroupMembers.slice(0, this.props.groupMembers.length),
                affectsLineItems
            );
        }
    };

    renderMembers() {
        if (
            this.props.permit.components
                ? this.props.permit.components.group_with_fees_and_dates
                : false
        ) {
            return this.props.groupMembers.map((member, index) => {
                const validation = filterValidationDataIndices(
                    this.props.validation,
                    index
                );

                return (
                    <GroupMemberItemWithDates
                        key={member.id}
                        member={member}
                        index={index}
                        permit={this.props.permit}
                        divisionId={this.props.divisionId}
                        handleMemberInfoChange={this.handleMemberInfoChange}
                        focusOnNextAdd={this.state.focusOnNextAdd}
                        isDisabled={this.props.isDisabled}
                        isDateDisabled={
                            index === 0 && this.props.isFirstGroupMemberDateDisabled
                        }
                        renderDates
                        removeMember={this.removeMember}
                        entryDate={this.props.entryDate}
                        dateMap={createAvailabilityRangeMap(
                            this.props.entryDate,
                            this.props.exitDate
                        )}
                        passOptions={this.props.passOptions}
                        alwaysDisplayPassOptions={this.state.alwaysDisplayPassOptions}
                        disableRemove={index === 0 || this.props.disableGroupSizeChanges}
                        isFirstNameRequired={
                            !!this.props.validationRules[types.IsFirstNameEmpty]
                        }
                        isLastNameRequired={
                            !!this.props.validationRules[types.IsLastNameEmpty]
                        }
                        displayStartDateError={
                            !!validation[types.IsGroupMemberStartDateEmpty]
                        }
                        displayEndDateError={
                            !!validation[types.IsGroupMemberEndDateEmpty]
                        }
                        isHighUseFeeApplicable={this.props.isHighUseFeeApplicable}
                    />
                );
            });
        }

        if (get(this.props.permit.components, 'group_with_fees_only', false)) {
            return this.props.groupMembers.map((member, index) => (
                <GroupMemberItemWithDates
                    key={member.id}
                    member={member}
                    index={index}
                    permit={this.props.permit}
                    divisionId={this.props.divisionId}
                    handleMemberInfoChange={this.handleMemberInfoChange}
                    focusOnNextAdd={this.state.focusOnNextAdd}
                    isDisabled={this.props.isDisabled}
                    isDateDisabled={
                        index === 0 && this.props.isFirstGroupMemberDateDisabled
                    }
                    renderDates={false}
                    removeMember={this.removeMember}
                    entryDate={this.props.entryDate}
                    dateMap={createAvailabilityRangeMap(
                        this.props.entryDate,
                        this.props.exitDate
                    )}
                    passOptions={this.props.passOptions}
                    alwaysDisplayPassOptions={this.state.alwaysDisplayPassOptions}
                    disableRemove={index === 0 || this.props.disableGroupSizeChanges}
                    isFirstNameRequired={
                        !!this.props.validationRules[types.IsFirstNameEmpty]
                    }
                    isLastNameRequired={
                        !!this.props.validationRules[types.IsLastNameEmpty]
                    }
                    isHighUseFeeApplicable={this.props.isHighUseFeeApplicable}
                />
            ));
        }

        // default is group_member_list which should hit this last
        return this.props.groupMembers.map((member, index) => {
            // filter out correct validations
            const validation = filterValidationDataIndices(this.props.validation, index);

            return (
                <GroupMemberItem
                    key={member.id}
                    member={member}
                    index={index}
                    handleMemberInfoChange={this.handleMemberInfoChange}
                    focusOnNextAdd={this.state.focusOnNextAdd}
                    isDisabled={this.props.isDisabled}
                    readOnly={this.props.readOnly}
                    removeMember={this.removeMember}
                    validation={validation}
                    collectBirthdate={this.props.permit.components.group_with_birthdates}
                    collectAge={this.props.permit.components.group_with_age}
                    disableRemove={index === 0 || this.props.disableGroupSizeChanges}
                    isFirstNameRequired={
                        !!this.props.validationRules[types.IsFirstNameEmpty]
                    }
                    isLastNameRequired={
                        !!this.props.validationRules[types.IsLastNameEmpty]
                    }
                    isAgeRequired={!!this.props.validationRules[types.IsAgeEmpty]}
                    isBirthdateRequired={
                        !!this.props.validationRules[types.IsBirthdateEmpty]
                    }
                />
            );
        });
    }

    renderAddMemberButton() {
        return (
            <div className="rec-form-item-wrap">
                <Button
                    type="button"
                    appearance="link"
                    className="addOneMemberLink"
                    onClick={this.addMember}
                    iconBeforeElement={<Icon iconName="add-circle" />}
                >
                    Add another member
                </Button>
            </div>
        );
    }

    renderChangeDetails() {
        if (!this.props.disableGroupSizeChanges) {
            // if quota isn't determined by group size, we don't display this button or text here.
            return null;
        }

        if (this.props.isQuotaModifyAllowed) {
            return (
                <div className="rec-form-item-wrap">
                    <Button
                        appearance="link"
                        iconBeforeElement={<Icon iconName="edit" />}
                        onClick={this.onChangeDetailsClick}
                        className="change-details-link"
                    >
                        {this.props.modifyDetailsButtonText}
                    </Button>
                </div>
            );
        }
        return NoQuotaMods;
    }

    renderGroupSizeInfoModal() {
        const stayLimitDays =
            readRuleValue('StayLimit', this.props.permit.rules) / 24 / 60 / 60;
        const highSeasonStart = readRuleAsMoment(
            'HighUseSeasonStart',
            this.props.permit.rules,
            this.props.permit.time_zone
        ).format('MMMM D');
        const highSeasonEnd = readRuleAsMoment(
            'HighUseSeasonEnd',
            this.props.permit.rules,
            this.props.permit.time_zone
        ).format('MMMM D');

        let { maxMemberQuota, maxGroupSize, maxGroupSizeCommercial } = this.props;
        if (this.props.permit.id === '234623') {
            // mid fork
            // we half the quota here because a group member could leave midtrip and be replaced.
            maxMemberQuota = Math.floor(maxMemberQuota / 2);
            maxGroupSize = Math.floor(maxGroupSize / 2);
            maxGroupSizeCommercial = Math.floor(maxGroupSizeCommercial / 2);
        }

        const modalProps = {
            isOpen: this.state.showGroupSizeModal,
            closeModal: () => this.setState({ showGroupSizeModal: false }),
            title: 'How Group Size Affects your stay limits',
        };

        return (
            <InfoModal {...modalProps}>
                <div className="per-group-size-modal">
                    <p>
                        Depending on your group size during the Lottery Control Season,
                        the maximum amount of time you are allowed to stay on your trip
                        depends on your largest group size present based on the table
                        below.
                    </p>
                    <p>
                        During the Lottery Control Season, ({highSeasonStart} -{' '}
                        {highSeasonEnd}) group size determines trip length. Outside the
                        Lottery Control Season the maximum group size is {maxMemberQuota}{' '}
                        and the maximum trip length is {stayLimitDays} days.
                    </p>
                    <FlexRow>
                        <FlexCol md={6}>
                            <div className="per-group-size-table-header">
                                Non-Commercial Trips
                            </div>
                            <table className="rec-table-light per-group-size-table">
                                <thead>
                                    <tr>
                                        <th>Group Size</th>
                                        <th>Max Trip Length</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>21 - {maxGroupSize} people</td>
                                        <td>6 days (5 nights)</td>
                                    </tr>
                                    <tr>
                                        <td>11 - 20 people</td>
                                        <td>7 days (6 nights)</td>
                                    </tr>
                                    <tr>
                                        <td>1 - 10 people</td>
                                        <td>8 days (7 nights)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </FlexCol>
                        <FlexCol md={6}>
                            <div className="per-group-size-table-header">
                                Commercial Trips
                            </div>
                            <table className="rec-table-light per-group-size-table">
                                <thead>
                                    <tr>
                                        <th>Group Size</th>
                                        <th>Max Trip Length</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td>21 - {maxGroupSizeCommercial} people</td>
                                        <td>6 days (5 nights)</td>
                                    </tr>
                                    <tr>
                                        <td>11 - 20 people</td>
                                        <td>7 days (6 nights)</td>
                                    </tr>
                                    <tr>
                                        <td>1 - 10 people</td>
                                        <td>8 days (7 nights)</td>
                                    </tr>
                                </tbody>
                            </table>
                        </FlexCol>
                    </FlexRow>
                </div>
            </InfoModal>
        );
    }

    render() {
        return (
            <div
                data-testid="group-member-list"
                className="per-group-member-form per-section-group"
            >
                {this.renderGroupSizeInfoModal()}
                <h2 className="h4">Group Members</h2>
                {this.getHeaderText()}
                <FlexRow className="mb-3">
                    <FlexCol sm="variable" alignSelf="center">
                        <NumberField
                            id="total-group-size-input"
                            isDisabled={this.props.disableGroupSizeChanges}
                            max={this.props.maxMemberQuota}
                            min={this.props.minMemberQuota}
                            onChange={this.handleGroupSizeChange}
                            step={1}
                            units="guests"
                            value={Math.max(
                                this.props.minMemberQuota,
                                this.props.groupMembers.length
                            )}
                            visualLabel="Total Group Size"
                        />
                    </FlexCol>
                </FlexRow>
                <div className="per-form-group">{this.renderMembers()}</div>
                {!this.props.disableGroupSizeChanges
                    ? this.renderAddMemberButton()
                    : null}
                {this.renderChangeDetails()}
            </div>
        );
    }
}

export default GroupMemberList;
